import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { GapSmall } from 'components/GapSmall';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { UI_DATE_FORMAT_LONG, UI_TIME_FORMAT } from 'lib/constants';
import { formatDate, numberToDate, openUrl } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  NAVIGATE_MODAL,
  doSetupApiClose,
  doSetupApiCreateUserPreflight,
  doSetupApiCreateWebhookPreflight,
  doSetupApiDeleteUser,
  doSetupApiDeleteWebhook,
} from 'store/actions';
import {
  selectApiSettingsUsers,
  selectApiSettingsWebhooks,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { API_DOCS_WEBSITE } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsApi
>;

export const ApiScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId);
  const users = useSelector(selectApiSettingsUsers) || [];
  const webhooks = useSelector(selectApiSettingsWebhooks) || [];

  React.useEffect(() => {
    return () => {
      dispatch(doSetupApiClose());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!organisationId) {
      navigation.goBack();
    }
  }, [navigation, organisationId]);

  if (!organisationId) {
    return null;
  }

  const webhookErrorString: Record<number, string> = {
    6: 'Host Not Resolved',
    28: 'Timed Out',
  };

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="API & Webhooks"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <Card
        explanation="The RiskBase API allows you to directly consume data in RiskBase. Get an API token for your organisation from this page and head over to
          our API docs to get started."
      />
      <Button
        text="API Documentation"
        onPress={() => openUrl(API_DOCS_WEBSITE)}
        style={{ alignSelf: 'center' }}
      />
      <SeparatorHorizontal />
      <CardGeneric>
        <Txt weight="bold">API Users</Txt>
        <Txt>
          Each API user has a unique token to authenticate with the RiskBase
          API. Include the token in every API request
        </Txt>
      </CardGeneric>
      <GapSmall>
        {!!users.length && (
          <Card>
            {users.map((user) => (
              <CardRow
                key={user.id}
                label={user.name}
                labelAlignTop
                onPress={() =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.SettingsApiUser,
                      params: {
                        type: 'existing',
                        data: {
                          id: user.id,
                          organisationId,
                          name: user.name,
                          createdTs: user.createdTs,
                          expiredTs: user.expiredTs,
                          expireTs: user.expireTs,
                          lastAccessedTs: user.lastAccessedTs,
                        },
                      },
                    }),
                  )
                }
                indentActions
                menuOptions={[
                  {
                    text: 'Open API User',
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.SettingsApiUser,
                          params: {
                            type: 'existing',
                            data: {
                              id: user.id,
                              organisationId,
                              name: user.name,
                              createdTs: user.createdTs,
                              expiredTs: user.expiredTs,
                              expireTs: user.expireTs,
                              lastAccessedTs: user.lastAccessedTs,
                            },
                          },
                        }),
                      ),
                    icon: ICONS.GearComplexCodeLight,
                  },
                  {
                    text: 'Delete API User',
                    onPress: () =>
                      dispatch(
                        doSetupApiDeleteUser({
                          organisationId,
                          id: user.id,
                          name: user.name,
                        }),
                      ),
                    icon: ICONS.TrashCanLight,
                    destructive: true,
                  },
                ]}
                labelColor={user.expiredTs ? theme.colors.red : undefined}
              >
                {user.expiredTs ? (
                  <Txt size="xsmall" style={{ color: theme.colors.red }}>
                    <Txt size="xsmall" weight="bold">
                      Expired
                    </Txt>{' '}
                    {user.lastAccessedTs
                      ? `${formatDate(
                          numberToDate(user.expiredTs),
                          UI_DATE_FORMAT_LONG,
                        )} ${formatDate(
                          numberToDate(user.expiredTs),
                          UI_TIME_FORMAT,
                        )}`
                      : 'Never'}
                  </Txt>
                ) : (
                  <Txt size="xsmall">
                    <Txt size="xsmall" weight="bold">
                      Accessed
                    </Txt>{' '}
                    {user.lastAccessedTs
                      ? `${formatDate(
                          numberToDate(user.lastAccessedTs),
                          UI_DATE_FORMAT_LONG,
                        )} ${formatDate(
                          numberToDate(user.lastAccessedTs),
                          UI_TIME_FORMAT,
                        )}`
                      : 'Never'}
                  </Txt>
                )}
              </CardRow>
            ))}
          </Card>
        )}
        <Card>
          <CardRow
            label="Create API user..."
            labelColor={theme.colors.blue}
            icon={ICONS.PlusLight}
            iconColor={theme.colors.blue}
            onPress={() =>
              dispatch(doSetupApiCreateUserPreflight({ organisationId }))
            }
          />
        </Card>
      </GapSmall>
      <SeparatorHorizontal />
      <CardGeneric>
        <Txt weight="bold">Webhooks</Txt>
        <Txt>
          Webhooks allow external services to be notified when certain events in
          RiskBase happen. When an event happens, RiskBase will send a POST
          request to the URLs you provide
        </Txt>
      </CardGeneric>
      <GapSmall>
        {!!webhooks.length && (
          <Card>
            {webhooks.map((webhook) => {
              const errorString = webhook.lastRequestErrorNumber
                ? webhookErrorString[webhook.lastRequestErrorNumber || -1]
                : '';
              return (
                <CardRow
                  key={webhook.id}
                  label={webhook.url}
                  labelAlignTop
                  onPress={() =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.SettingsApiWebhook,
                        params: {
                          type: 'existing',
                          data: {
                            id: webhook.id,
                            organisationId,
                            url: webhook.url,
                            createdTs: webhook.createdTs,
                            hasSecret: webhook.hasSecret,
                            lastRequestTs: webhook.lastRequestTs,
                            lastRequestStatus: webhook.lastRequestStatus,
                            lastRequestErrorNumber:
                              webhook.lastRequestErrorNumber,
                          },
                        },
                      }),
                    )
                  }
                  indentActions
                  menuOptions={[
                    {
                      text: 'Open Webhook',
                      onPress: () =>
                        dispatch(
                          NAVIGATE_MODAL({
                            screen: ModalScreens.SettingsApiWebhook,
                            params: {
                              type: 'existing',
                              data: {
                                id: webhook.id,
                                organisationId,
                                url: webhook.url,
                                createdTs: webhook.createdTs,
                                hasSecret: webhook.hasSecret,
                                lastRequestTs: webhook.lastRequestTs,
                                lastRequestStatus: webhook.lastRequestStatus,
                                lastRequestErrorNumber:
                                  webhook.lastRequestErrorNumber,
                              },
                            },
                          }),
                        ),
                      icon: ICONS.WebhookLight,
                    },
                    {
                      text: 'Delete Webhook',
                      onPress: () =>
                        dispatch(
                          doSetupApiDeleteWebhook({
                            organisationId,
                            id: webhook.id,
                            url: webhook.url,
                          }),
                        ),
                      icon: ICONS.TrashCanLight,
                      destructive: true,
                    },
                  ]}
                >
                  <Txt size="xsmall">
                    <Txt size="xsmall" weight="bold">
                      Last Ran
                    </Txt>{' '}
                    {webhook.lastRequestTs
                      ? `${formatDate(
                          numberToDate(webhook.lastRequestTs),
                          UI_DATE_FORMAT_LONG,
                        )} ${formatDate(
                          numberToDate(webhook.lastRequestTs),
                          UI_TIME_FORMAT,
                        )}`
                      : 'Never'}
                  </Txt>
                  {webhook.lastRequestStatus ? (
                    <Txt size="xsmall">
                      <Txt size="xsmall" weight="bold">
                        Status
                      </Txt>{' '}
                      <Txt
                        size="xsmall"
                        style={{
                          color: webhook.lastRequestStatus
                            .toString()
                            .startsWith('2')
                            ? theme.colors.green
                            : theme.colors.red,
                        }}
                      >
                        {webhook.lastRequestStatus.toString()}
                      </Txt>
                    </Txt>
                  ) : errorString ? (
                    <Txt size="xsmall">
                      <Txt size="xsmall" weight="bold">
                        Status
                      </Txt>{' '}
                      <Txt
                        size="xsmall"
                        style={{
                          color: theme.colors.red,
                        }}
                      >
                        {errorString}
                      </Txt>
                    </Txt>
                  ) : null}
                </CardRow>
              );
            })}
          </Card>
        )}
        <Card>
          <CardRow
            label="Create webhook..."
            labelColor={theme.colors.blue}
            icon={ICONS.PlusLight}
            iconColor={theme.colors.blue}
            onPress={() =>
              dispatch(doSetupApiCreateWebhookPreflight({ organisationId }))
            }
          />
        </Card>
      </GapSmall>
    </Page>
  );
};
